import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	stack: {
		position: 'absolute',
		left: 0,
		bottom: 0,
		transform: `translateX(calc(-100% - ${token('space.100')}))`,
		width: '300px',
		maxWidth: '80vw',
		minWidth: 0,
		height: 'auto',
		transitionProperty: 'height',
		transitionDuration: token('motion.duration.medium'),
		transitionTimingFunction: token('motion.easing.inout.bold'),
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0ms',
		},
	},
	item: {
		position: 'absolute',
		left: 0,
		bottom: 0,
		width: '100%',
		transformOrigin: 'bottom center',
		transitionProperty: 'transform',
		transitionDuration: token('motion.duration.medium'),
		transitionTimingFunction: token('motion.easing.inout.bold'),
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0ms',
		},
	},
	front: {
		zIndex: 300,
		transform: 'translateY(0) scale(1)',
	},
	middle: {
		zIndex: 200,
		transform: `translateY(calc(-1 * ${token('space.100')})) scale(0.95)`,
	},
	back: {
		zIndex: 100,
		transform: `translateY(calc(-1 * ${token('space.200')})) scale(0.9)`,
	},
	expandedFront: {
		zIndex: 300,
		transform: 'translateY(0) scale(1)',
	},
	expandedMiddle: {
		transform: `translateY(calc(-1 * (var(--nudge-stack-preceding-height, 0px) + ${token('space.100')}))) scale(1)`,
	},
	expandedBack: {
		transform: `translateY(calc(-1 * (var(--nudge-stack-preceding-height, 0px) + ${token('space.200')}))) scale(1)`,
	},
});

const collapsedItemStyles = [styles.front, styles.middle, styles.back];
const expandedItemStyles = [styles.expandedFront, styles.expandedMiddle, styles.expandedBack];
type NudgeStackCustomProperties = React.CSSProperties & {
	'--nudge-stack-preceding-height'?: string;
};

export type NudgeStackProps = Readonly<{
	children: ReactNode;
	onExpandedChange?: (isExpanded: boolean) => void;
}>;

/** Layout-only container. Children are supplied in highest-to-lowest priority order. */
export function NudgeStack({ children, onExpandedChange }: NudgeStackProps): React.JSX.Element {
	const stackItems = useMemo(() => React.Children.toArray(children), [children]);
	const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
	const [itemHeights, setItemHeights] = useState<number[]>([]);
	const [isPointerInside, setIsPointerInside] = useState(false);
	const [isFocusWithin, setIsFocusWithin] = useState(false);
	const isExpanded = isPointerInside || isFocusWithin;

	useEffect(() => {
		onExpandedChange?.(isExpanded);
	}, [isExpanded, onExpandedChange]);

	useEffect(
		() => () => {
			onExpandedChange?.(false);
		},
		[onExpandedChange],
	);

	const measureItems = useCallback(() => {
		const nextHeights = stackItems.map((_, index) => itemRefs.current[index]?.offsetHeight ?? 0);
		// Preserve the existing array reference when measurements are unchanged so React can skip a
		// redundant render.
		setItemHeights((currentHeights) =>
			currentHeights.length === nextHeights.length &&
			currentHeights.every((height, index) => height === nextHeights[index])
				? currentHeights
				: nextHeights,
		);
	}, [stackItems]);

	useEffect(() => {
		measureItems();

		if (typeof ResizeObserver === 'undefined') {
			return;
		}

		// Responsive text wrapping or asynchronously updated custom content can resize a card after
		// mount, so keep the stack offsets in sync.
		const resizeObserver = new ResizeObserver(measureItems);
		// Observe only current cards because the ref array can retain unused slots after a refill.
		itemRefs.current.slice(0, stackItems.length).forEach((item) => {
			if (item) {
				resizeObserver.observe(item);
			}
		});

		return () => resizeObserver.disconnect();
	}, [measureItems, stackItems.length]);

	const frontHeight = itemHeights[0];
	const totalCardHeight = itemHeights.reduce((total, height) => total + height, 0);
	// Add one space.100 gap between each card to the combined expanded card heights.
	const expandedHeight =
		stackItems.length === 3
			? `calc(${totalCardHeight}px + ${token('space.200')})`
			: stackItems.length === 2
				? `calc(${totalCardHeight}px + ${token('space.100')})`
				: `${totalCardHeight}px`;
	const stackStyle: React.CSSProperties = {
		height: frontHeight ? (isExpanded ? expandedHeight : `${frontHeight}px`) : undefined,
	};

	return (
		<Box
			testId="platform-ai-proactive-nudge-stack"
			xcss={styles.stack}
			style={stackStyle}
			onMouseEnter={() => setIsPointerInside(true)}
			onMouseLeave={() => setIsPointerInside(false)}
			onFocus={() => setIsFocusWithin(true)}
			onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
				const nextFocusedElement = event.relatedTarget;
				// Keep the stack expanded while focus moves between cards; collapse only when it leaves.
				if (
					!(nextFocusedElement instanceof Node) ||
					!event.currentTarget.contains(nextFocusedElement)
				) {
					setIsFocusWithin(false);
				}
			}}
		>
			{stackItems.map((child, index) => {
				// Preserve a card's component instance as higher-priority cards leave the stack so its
				// lifecycle timers are not restarted when it moves to a different position.
				const itemKey = React.isValidElement(child) ? child.key : null;
				// Move each card above the combined heights of all higher-priority cards.
				const precedingHeight = itemHeights
					.slice(0, index)
					.reduce((total, height) => total + height, 0);
				const itemStyle: NudgeStackCustomProperties = {
					'--nudge-stack-preceding-height': `${precedingHeight}px`,
				};

				return (
					<Box
						key={itemKey ?? `nudge-stack-item-${index}`}
						testId={`platform-ai-proactive-nudge-stack-item-${index}`}
						xcss={cx(
							styles.item,
							isExpanded
								? (expandedItemStyles[index] ?? styles.expandedBack)
								: (collapsedItemStyles[index] ?? styles.back),
						)}
						style={itemStyle}
						ref={(element: HTMLDivElement | null) => {
							itemRefs.current[index] = element;
						}}
					>
						{child}
					</Box>
				);
			})}
		</Box>
	);
}
